// dashboard/alertActions.js
// Supabase mutations behind the roster's Mark Reviewed/reassign actions,
// pulled out of App.js so they're unit-testable in isolation (mocked
// supabase client, no need to render the whole dashboard). App.js's own
// handlers stay thin wrappers: call the mutation, only touch local React
// state once `success` confirms the write actually happened.
//
// supabase-js doesn't throw on a query-level failure (RLS denial, a stale
// session, a network blip) — it resolves with { error }. Every mutation
// here follows handleDismissAlert's already-fixed pattern (2026-08-05,
// "Dismiss doesn't work" report): `.select('id')` the write and treat
// "no error but zero rows" as a failure too, not just an explicit error,
// since an RLS-denied update also returns that shape.

import { supabase } from '../supabase';

// dismissAlertMutation/reactivateAlertMutation go through dismiss_alert()/
// reactivate_alert() (migrations 197/204/209) rather than a direct table
// update — resolved_by/acknowledged_by/dismissed_by are server-derived
// from the authenticated session, not client-supplied. Direct client
// updates to these columns are also blocked at the grant level, so the
// old .update({...}) form would fail regardless.
//
// Corrected 2026-09-08 (Bryan, real usage): Dismiss previously called
// resolve_alert() — full closure, `is_resolved = true`. That was wrong:
// dismissing meant the patient fell all the way into the undifferentiated
// Green — Stable bucket with no way back short of a new survey. Dismiss
// now means "reviewed, out of the active queue until the next survey" —
// dismiss_alert() only sets dismissed_at/dismissed_by, never touches
// severity/current_severity/peak/is_resolved. True resolution now only
// happens through the automatic 2/2/2 recovery mechanism.
//
// Merged 2026-09-09 (Bryan): Dismiss, Snooze, and Acknowledge all did the
// same underlying thing — move an alert out of the active queue, with
// only cosmetic differences in when it resurfaces. Collapsed into one
// "Mark reviewed" action (still called dismiss_alert()/dismissAlertMutation
// internally — the button label changed, not the underlying concept).
// steppedDown mirrors the old acknowledgeAlertMutation's return shape:
// dismiss_alert() now also performs Immediate Review's step-down release
// when reviewing an alert whose recovery streak already met threshold.
export async function dismissAlertMutation(alertId, _actorProfileId) {
  const { error, data } = await supabase.rpc('dismiss_alert', { p_alert_id: alertId });
  return { success: !error && !!data?.dismissed, error, steppedDown: !!data?.stepped_down };
}

export async function reactivateAlertMutation(alertId) {
  const { error, data } = await supabase.rpc('reactivate_alert', { p_alert_id: alertId });
  // companionAlertId (migration 206) is present only when a linked CHF/
  // chronic_respiratory<->SRR companion alert was also reactivated
  // server-side — the caller must patch both, not just the one clicked,
  // or the companion looks dismissed until the next full refetch.
  return {
    success: !error && !!data?.reactivated,
    error,
    companionAlertId: data?.companion_reactivated ? data?.companion_alert_id : null,
  };
}

export async function reassignDoctorMutation(conditionId, vetId) {
  const { error, data } = await supabase
    .from('conditions')
    .update({ assigned_by: vetId })
    .eq('id', conditionId)
    .select('id');
  return { success: !error && !!data?.length, error };
}
